import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { ArrowLeft, Bot, BrainCircuit, ImagePlus, KeyRound, Link2, RotateCcw, Sparkles, Trash2, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import sheetLogo from '../../assets/images/sheetlogo.png';
import { storage } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { sheetService } from '../../services/sheetService';
import { sheetWorkspaceService } from '../../services/sheetWorkspaceService';
import { userService } from '../../services/userService';
import organizationService from '../../services/organizationService';
import {
  getWorkspacePath,
  getWorkspaceUrl,
  getWorkspacesPath,
  isDefaultSheetsHost,
  normalizeWorkspaceCustomDomain
} from '../../utils/sheetDomains';
import {
  DEFAULT_WORKSPACE_PRIMARY_COLOR,
  normalizeWorkspacePrimaryColor
} from '../../utils/sheetWorkspaceTheme';
import SheetsHeader from './SheetsHeader';
import WorkspaceDashboardBuilder from './WorkspaceDashboardBuilder';
import { sanitizeWorkspaceDashboardConfig, seedWorkspaceDashboardConfig } from '../../utils/workspaceDashboard';

const normalizeWorkspaceSlug = (value, fallback = 'workspace') => {
  const normalized = `${value || ''}`
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-')
    .slice(0, 64);
  return normalized || fallback;
};

const parseSkills = (raw = '') => Array.from(new Set(
  `${raw || ''}`
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
));

const parseEmailList = (raw = '') => `${raw || ''}`
  .split(',')
  .map((item) => item.trim().toLowerCase())
  .filter(Boolean)
  .filter((item, index, all) => all.indexOf(item) === index)
  .filter((email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));

const WorkspaceBuilder = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { workspaceSlug: routeWorkspaceSlug } = useParams();
  const { user, userProfile } = useAuth();
  const isEditing = Boolean(`${routeWorkspaceSlug || ''}`.trim());

  const [workspace, setWorkspace] = useState(null);
  const [workspaceTitle, setWorkspaceTitle] = useState('');
  const [workspaceDescription, setWorkspaceDescription] = useState('');
  const [workspacePrimaryColor, setWorkspacePrimaryColor] = useState(DEFAULT_WORKSPACE_PRIMARY_COLOR);
  const [workspaceSlug, setWorkspaceSlug] = useState('');
  const [workspaceCustomDomain, setWorkspaceCustomDomain] = useState('');
  const [workspaceVisibility, setWorkspaceVisibility] = useState('private');
  const [organizationIds, setOrganizationIds] = useState([]);
  const [availableOrganizations, setAvailableOrganizations] = useState([]);
  const [adminBuilderAccessEnabled, setAdminBuilderAccessEnabled] = useState(false);
  const [workspaceIsTemplate, setWorkspaceIsTemplate] = useState(false);
  const [workspaceAccess, setWorkspaceAccess] = useState('view');
  const [allowCrossSheetReferences, setAllowCrossSheetReferences] = useState(true);
  const [workspaceLogoFile, setWorkspaceLogoFile] = useState(null);
  const [workspaceLogoRemoved, setWorkspaceLogoRemoved] = useState(false);
  const [workspaceLogoPreview, setWorkspaceLogoPreview] = useState('');
  const [workspaceSlugEdited, setWorkspaceSlugEdited] = useState(false);
  const [workspaceCustomDomainEdited, setWorkspaceCustomDomainEdited] = useState(false);
  const [workspaceSlugStatus, setWorkspaceSlugStatus] = useState({ state: 'idle', message: 'Enter a workspace path.' });
  const [aiEnabled, setAiEnabled] = useState(false);
  const [aiProvider, setAiProvider] = useState('gemini');
  const [aiSkills, setAiSkills] = useState('');
  const [aiInstructions, setAiInstructions] = useState('');
  const [aiCredentialStatus, setAiCredentialStatus] = useState({ gemini: false, openai: false });
  const [aiCredentialStatusLoading, setAiCredentialStatusLoading] = useState(false);
  const [savingAiCredential, setSavingAiCredential] = useState('');
  const geminiApiKeyRef = useRef(null);
  const openAiApiKeyRef = useRef(null);
  const [agentsEnabled, setAgentsEnabled] = useState(false);
  const [agentName, setAgentName] = useState('');
  const [agentAutomationLevel, setAgentAutomationLevel] = useState('manual');
  const [agentInstructions, setAgentInstructions] = useState('');
  const [agentSkills, setAgentSkills] = useState('');
  const [allowedEmailsInput, setAllowedEmailsInput] = useState('');
  const [resolvedSharedUsers, setResolvedSharedUsers] = useState([]);
  const [selectedSharedUserIds, setSelectedSharedUserIds] = useState([]);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [workspaceSheets, setWorkspaceSheets] = useState([]);
  const [dashboardConfig, setDashboardConfig] = useState(() => seedWorkspaceDashboardConfig([]));
  const [activeBuilderPanel, setActiveBuilderPanel] = useState('setup');

  const assignmentSheetId = `${location.state?.assignSheetId || ''}`.trim();
  const assignmentSheetTitle = `${location.state?.assignSheetTitle || ''}`.trim();
  const explicitReturnTo = `${location.state?.returnTo || ''}`.trim();
  const workspacePathPrefix = isDefaultSheetsHost() ? '/workspace/' : '/sheets/workspace/';

  const logoPreviewSrc = workspaceLogoPreview
    || (!workspaceLogoRemoved && `${workspace?.logoUrl || ''}`.trim())
    || sheetLogo;

  const backPath = explicitReturnTo || (isEditing && workspace?.slug ? getWorkspacePath(workspace.slug) : getWorkspacesPath());
  const pageTitle = isEditing ? 'Edit Workspace' : 'Create Workspace';
  const pageDescription = isEditing
    ? 'Update route settings, access rules, AI defaults, and workspace branding in the full builder instead of a modal.'
    : 'Set the route, access rules, AI skills, and agent defaults for a workspace before sheets start accumulating inside it.';
  const normalizedPrimaryColor = normalizeWorkspacePrimaryColor(workspacePrimaryColor)
    || DEFAULT_WORKSPACE_PRIMARY_COLOR;
  const workspaceThemeStyle = {
    '--workspace-primary-color': normalizedPrimaryColor
  };

  const setupSummary = useMemo(() => {
    const items = [];
    if (aiEnabled) items.push(`${parseSkills(aiSkills).length || 1} AI skill preset${parseSkills(aiSkills).length === 1 ? '' : 's'}`);
    if (agentsEnabled) items.push(`${agentAutomationLevel} agent workflow`);
    if (allowCrossSheetReferences) items.push('cross-sheet references enabled');
    return items.length ? items.join(' | ') : 'Basic workspace setup';
  }, [agentAutomationLevel, agentsEnabled, aiEnabled, aiSkills, allowCrossSheetReferences]);

  useEffect(() => {
    let active = true;
    organizationService.getOrganizationsByIds(userProfile?.approvedOrganizationIds || [])
      .then((items) => { if (active) setAvailableOrganizations(items); })
      .catch(() => { if (active) setAvailableOrganizations([]); });
    return () => { active = false; };
  }, [userProfile?.approvedOrganizationIds]);

  useEffect(() => {
    if (workspaceSlugEdited) return;
    setWorkspaceSlug(normalizeWorkspaceSlug(workspaceTitle));
  }, [workspaceSlugEdited, workspaceTitle]);

  useEffect(() => {
    if (!workspaceLogoFile) {
      setWorkspaceLogoPreview('');
      return undefined;
    }

    const objectUrl = URL.createObjectURL(workspaceLogoFile);
    setWorkspaceLogoPreview(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [workspaceLogoFile]);

  useEffect(() => {
    if (!isEditing || !user?.uid) {
      setLoading(false);
      return;
    }

    let isActive = true;

    const loadWorkspace = async () => {
      setLoading(true);
      setLoadError('');

      try {
        const workspaceData = await sheetWorkspaceService.getWorkspaceBySlug(routeWorkspaceSlug);
        const [openUser, currentMembership] = await Promise.all([
          userService.getUserProfile(user.uid).catch(() => null),
          sheetWorkspaceService.getWorkspaceMember(workspaceData.id, user.uid).catch(() => null)
        ]);
        const isAdmin = `${openUser?.role || ''}`.trim() === 'admin';
        const isWorkspaceAdmin = `${currentMembership?.role || ''}`.trim() === 'admin';
        const canManageWorkspace = (
          workspaceData.userId === user.uid
          || isWorkspaceAdmin
          || (isAdmin && workspaceData.adminBuilderAccessEnabled === true)
        );

        if (!isActive) return;

        if (!canManageWorkspace) {
          setLoadError('You do not have permission to edit this workspace.');
          setWorkspace(null);
          return;
        }

        setWorkspace(workspaceData);
        setWorkspaceTitle(`${workspaceData.title || ''}`);
        setWorkspaceDescription(`${workspaceData.description || ''}`);
        setWorkspacePrimaryColor(
          normalizeWorkspacePrimaryColor(workspaceData.primaryColor) || DEFAULT_WORKSPACE_PRIMARY_COLOR
        );
        setWorkspaceSlug(`${workspaceData.slug || ''}`);
        setWorkspaceCustomDomain(`${workspaceData.customDomain || ''}`);
        setWorkspaceVisibility(workspaceData.visibility === 'restricted' ? 'restricted' : 'private');
        setOrganizationIds(Array.isArray(workspaceData.organizationIds) ? workspaceData.organizationIds : []);
        setAdminBuilderAccessEnabled(workspaceData.adminBuilderAccessEnabled === true);
        setWorkspaceIsTemplate(workspaceData.isTemplate === true);
        setWorkspaceAccess(`${workspaceData.defaultCollaboratorAccess || 'view'}`.trim() || 'view');
        setAllowCrossSheetReferences(workspaceData.allowCrossSheetReferences !== false);
        setWorkspaceLogoFile(null);
        setWorkspaceLogoRemoved(false);
        setWorkspaceSlugEdited(true);
        setWorkspaceCustomDomainEdited(false);
        setAiEnabled(workspaceData.aiConfig?.enabled === true);
        setAiProvider(workspaceData.aiConfig?.provider === 'openai' ? 'openai' : 'gemini');
        setAiSkills(Array.isArray(workspaceData.aiConfig?.skills) ? workspaceData.aiConfig.skills.join(', ') : '');
        setAiInstructions(`${workspaceData.aiConfig?.instructions || ''}`);
        setAgentsEnabled(workspaceData.agentConfig?.enabled === true);
        setAgentName(`${workspaceData.agentConfig?.defaultAgentName || ''}`);
        setAgentAutomationLevel(`${workspaceData.agentConfig?.automationLevel || 'manual'}`.trim() || 'manual');
        setAgentInstructions(`${workspaceData.agentConfig?.instructions || ''}`);
        setAgentSkills(Array.isArray(workspaceData.agentConfig?.skills) ? workspaceData.agentConfig.skills.join(', ') : '');
        setAiCredentialStatusLoading(true);
        sheetWorkspaceService.getAiCredentialStatus(workspaceData.id)
          .then((status) => {
            if (isActive) {
              setAiCredentialStatus({
                gemini: status?.gemini === true,
                openai: status?.openai === true
              });
            }
          })
          .catch(() => {
            if (isActive) setAiCredentialStatus({ gemini: false, openai: false });
          })
          .finally(() => {
            if (isActive) setAiCredentialStatusLoading(false);
          });

        const [members, workspaceSheetResults] = await Promise.all([
          sheetWorkspaceService.getWorkspaceMembers(workspaceData.id).catch(() => []),
          sheetService.getSheetsByWorkspaceIds([workspaceData.id]).catch(() => [])
        ]);
        if (!isActive) return;
        const loadedSheets = (workspaceSheetResults || []).filter((sheet) => `${sheet?.workspaceId || ''}` === workspaceData.id);
        setWorkspaceSheets(loadedSheets);
        setDashboardConfig(sanitizeWorkspaceDashboardConfig(workspaceData.dashboardConfig, loadedSheets));

        const nonOwnerMembers = members.filter((member) => member.role !== 'owner');
        const memberUsers = nonOwnerMembers
          .filter((member) => member.userId)
          .map((member) => ({
            uid: member.userId,
            email: `${member.email || ''}`.trim().toLowerCase(),
            displayName: `${member.displayName || ''}`.trim()
          }));

        setAllowedEmailsInput(
          nonOwnerMembers
            .map((member) => `${member.email || ''}`.trim().toLowerCase())
            .filter(Boolean)
            .join(', ')
        );
        setResolvedSharedUsers(memberUsers);
        setSelectedSharedUserIds(memberUsers.map((member) => member.uid));
      } catch (error) {
        if (!isActive) return;
        setLoadError(error?.message || 'Failed to load workspace.');
        setWorkspace(null);
      } finally {
        if (isActive) setLoading(false);
      }
    };

    loadWorkspace();

    return () => {
      isActive = false;
    };
  }, [isEditing, routeWorkspaceSlug, user?.uid]);

  useEffect(() => {
    const normalizedSlug = normalizeWorkspaceSlug(workspaceSlug, '');
    if (!normalizedSlug) {
      setWorkspaceSlugStatus({ state: 'idle', message: 'Enter a workspace path.' });
      return undefined;
    }

    if (isEditing && normalizedSlug === `${workspace?.slug || ''}`.trim()) {
      setWorkspaceSlugStatus({ state: 'available', message: 'Current workspace path.' });
      return undefined;
    }

    let isActive = true;
    setWorkspaceSlugStatus({ state: 'checking', message: 'Checking availability...' });

    const timeoutId = setTimeout(async () => {
      try {
        const existingWorkspace = await sheetWorkspaceService.getWorkspaceBySlug(normalizedSlug);
        if (!isActive) return;
        if (isEditing && existingWorkspace?.id === workspace?.id) {
          setWorkspaceSlugStatus({ state: 'available', message: 'Current workspace path.' });
          return;
        }
        setWorkspaceSlugStatus({ state: 'unavailable', message: 'Path already exists. Edit the path to continue.' });
      } catch (_error) {
        if (!isActive) return;
        setWorkspaceSlugStatus({ state: 'available', message: 'Path is available.' });
      }
    }, 250);

    return () => {
      isActive = false;
      clearTimeout(timeoutId);
    };
  }, [isEditing, workspace?.id, workspace?.slug, workspaceSlug]);

  useEffect(() => {
    const shouldResolveUsers = (
      user?.uid
      && workspaceVisibility === 'restricted'
      && workspaceIsTemplate !== true
    );

    if (!shouldResolveUsers) {
      if (workspaceVisibility !== 'restricted' || workspaceIsTemplate === true) {
        setResolvedSharedUsers([]);
        setSelectedSharedUserIds([]);
      }
      return;
    }

    const emails = parseEmailList(allowedEmailsInput);
    if (!emails.length) {
      setResolvedSharedUsers([]);
      setSelectedSharedUserIds([]);
      return;
    }

    let isActive = true;

    const loadResolvedSharedUsers = async () => {
      try {
        const users = await userService.findUsersByEmails(emails);
        if (!isActive) return;

        const matchedUsers = users
          .map((item) => ({
            uid: item.id || item.uid,
            email: `${item.email || ''}`.trim().toLowerCase(),
            displayName: `${item.displayName || ''}`.trim()
          }))
          .filter((item) => item.uid && emails.includes(item.email));

        setResolvedSharedUsers(matchedUsers);
        setSelectedSharedUserIds((prev) => {
          const existing = Array.isArray(prev) ? prev : [];
          const stillMatched = existing.filter((uid) => matchedUsers.some((matchedUser) => matchedUser.uid === uid));
          const newMatches = matchedUsers
            .map((matchedUser) => matchedUser.uid)
            .filter((uid) => !stillMatched.includes(uid));
          return [...stillMatched, ...newMatches];
        });
      } catch {
        if (!isActive) return;
        setResolvedSharedUsers([]);
      }
    };

    loadResolvedSharedUsers();

    return () => {
      isActive = false;
    };
  }, [allowedEmailsInput, user?.uid, workspaceIsTemplate, workspaceVisibility]);

  const handleToggleSharedUser = (userId) => {
    setSelectedSharedUserIds((prev) => (
      prev.includes(userId)
        ? prev.filter((item) => item !== userId)
        : [...prev, userId]
    ));
  };

  const syncWorkspaceMembers = async (workspaceId) => {
    const shouldSyncMembers = workspaceVisibility === 'restricted' && workspaceIsTemplate !== true;
    const currentMembers = await sheetWorkspaceService.getWorkspaceMembers(workspaceId);
    const existingNonOwnerMembers = currentMembers.filter((member) => member.role !== 'owner');
    const desiredUsers = shouldSyncMembers
      ? resolvedSharedUsers.filter((sharedUser) => selectedSharedUserIds.includes(sharedUser.uid))
      : [];

    const desiredUserIds = new Set(desiredUsers.map((member) => member.uid));
    await Promise.all(
      existingNonOwnerMembers
        .filter((member) => !desiredUserIds.has(member.userId))
        .map((member) => sheetWorkspaceService.removeMember(workspaceId, member.userId))
    );
    await Promise.all(desiredUsers.map((sharedUser) => {
      const existingMember = existingNonOwnerMembers.find((member) => member.userId === sharedUser.uid);
      return sheetWorkspaceService.addMember(workspaceId, {
        userId: sharedUser.uid,
        email: sharedUser.email,
        displayName: sharedUser.displayName,
        role: existingMember?.role || 'viewer',
        access: existingMember?.access || 'view'
      }, user?.uid);
    }));
  };

  const uploadWorkspaceLogo = async (workspaceId) => {
    if (!workspaceLogoFile || !user?.uid) return '';
    const safeName = `${Date.now()}_${workspaceLogoFile.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
    const logoRef = ref(storage, `sheets/workspaces/${workspaceId}/logo/${safeName}`);
    const snapshot = await uploadBytes(logoRef, workspaceLogoFile);
    return getDownloadURL(snapshot.ref);
  };

  const getAiKeyInput = (provider) => (
    provider === 'openai' ? openAiApiKeyRef.current : geminiApiKeyRef.current
  );

  const saveAiCredential = async (workspaceId, provider, { quiet = false } = {}) => {
    const input = getAiKeyInput(provider);
    const apiKey = `${input?.value || ''}`.trim();
    if (!apiKey) {
      if (!quiet) toast.error(`Enter a ${provider === 'openai' ? 'OpenAI' : 'Gemini'} API key.`);
      return false;
    }

    setSavingAiCredential(provider);
    try {
      await sheetWorkspaceService.setAiCredential(workspaceId, provider, apiKey);
      if (input) input.value = '';
      setAiCredentialStatus((current) => ({ ...current, [provider]: true }));
      if (!quiet) toast.success(`${provider === 'openai' ? 'OpenAI' : 'Gemini'} key stored in Cloud Secret Manager.`);
      return true;
    } finally {
      setSavingAiCredential('');
    }
  };

  const savePendingAiCredentials = async (workspaceId) => {
    for (const provider of ['gemini', 'openai']) {
      if (`${getAiKeyInput(provider)?.value || ''}`.trim()) {
        await saveAiCredential(workspaceId, provider, { quiet: true });
      }
    }
  };

  const handleSaveAiCredential = async (provider) => {
    if (!workspace?.id) {
      toast.error('Create the workspace before saving an API key.');
      return;
    }
    try {
      await saveAiCredential(workspace.id, provider);
    } catch (error) {
      toast.error(error?.message || 'Could not store the API key.');
    }
  };

  const handleRemoveAiCredential = async (provider) => {
    if (!workspace?.id || !aiCredentialStatus[provider]) return;
    const providerLabel = provider === 'openai' ? 'OpenAI' : 'Gemini';
    if (!window.confirm(`Remove the ${providerLabel} API key from this workspace?`)) return;

    setSavingAiCredential(provider);
    try {
      await sheetWorkspaceService.removeAiCredential(workspace.id, provider);
      setAiCredentialStatus((current) => ({ ...current, [provider]: false }));
      const input = getAiKeyInput(provider);
      if (input) input.value = '';
      toast.success(`${providerLabel} key removed.`);
    } catch (error) {
      toast.error(error?.message || 'Could not remove the API key.');
    } finally {
      setSavingAiCredential('');
    }
  };

  const handleSaveWorkspace = async () => {
    const cleanTitle = workspaceTitle.trim();
    const cleanSlug = normalizeWorkspaceSlug(workspaceSlug, '');
    const cleanCustomDomain = normalizeWorkspaceCustomDomain(workspaceCustomDomain);
    const cleanPrimaryColor = normalizeWorkspacePrimaryColor(workspacePrimaryColor);
    const previousSlug = `${workspace?.slug || ''}`.trim();
    const previousCustomDomain = normalizeWorkspaceCustomDomain(workspace?.customDomain);

    if (!cleanTitle) {
      toast.error('Workspace title is required.');
      return;
    }
    if (!cleanSlug) {
      toast.error('Workspace path is required.');
      return;
    }
    if (workspaceSlugStatus.state === 'unavailable') {
      toast.error('Choose an available workspace path.');
      return;
    }
    if (!cleanPrimaryColor) {
      toast.error('Enter a valid 3 or 6 digit primary color hex code.');
      return;
    }

    try {
      setSaving(true);

      if (isEditing && workspace?.id) {
        const workspaceSheets = await sheetService.getSheetsByWorkspaceIds([workspace.id]);
        const stillUsedOrganization = workspaceSheets
          .flatMap((sheet) => Array.isArray(sheet.organizationIds) ? sheet.organizationIds : [])
          .find((organizationId) => !organizationIds.includes(organizationId));
        if (stillUsedOrganization) {
          throw new Error('Remove this organisation from every sheet in the workspace before removing it from the workspace.');
        }
      }

      const basePayload = {
        title: cleanTitle,
        slug: cleanSlug,
        customDomain: cleanCustomDomain || undefined,
        description: workspaceDescription.trim(),
        organizationIds,
        primaryColor: cleanPrimaryColor,
        visibility: workspaceVisibility,
        adminBuilderAccessEnabled,
        isTemplate: workspaceIsTemplate,
        defaultCollaboratorAccess: workspaceAccess,
        allowCrossSheetReferences,
        aiConfig: {
          enabled: aiEnabled,
          provider: aiProvider,
          skills: parseSkills(aiSkills),
          instructions: aiInstructions.trim()
        },
        agentConfig: {
          enabled: agentsEnabled,
          defaultAgentName: agentName.trim(),
          automationLevel: agentAutomationLevel,
          instructions: agentInstructions.trim(),
          skills: parseSkills(agentSkills)
        },
        dashboardConfig,
        dashboardSheets: workspaceSheets
      };

      let nextWorkspace = workspace;

      if (isEditing && workspace?.id) {
        const basicUpdates = {
          title: cleanTitle,
          description: workspaceDescription.trim(),
          organizationIds,
          primaryColor: cleanPrimaryColor,
          visibility: workspaceVisibility,
          adminBuilderAccessEnabled,
          isTemplate: workspaceIsTemplate,
          defaultCollaboratorAccess: workspaceAccess,
          allowCrossSheetReferences,
          aiConfig: basePayload.aiConfig,
          agentConfig: basePayload.agentConfig,
          dashboardConfig,
          dashboardSheets: workspaceSheets
        };
        const routingUpdates = {};

        if (cleanSlug !== previousSlug) {
          routingUpdates.slug = cleanSlug;
        }
        if (workspaceCustomDomainEdited && cleanCustomDomain !== previousCustomDomain) {
          routingUpdates.customDomain = cleanCustomDomain || null;
        }

        await sheetWorkspaceService.updateWorkspaceBasics(workspace.id, basicUpdates);
        if (Object.keys(routingUpdates).length) {
          await sheetWorkspaceService.updateWorkspaceRouting(workspace.id, routingUpdates);
        }

        if (workspaceLogoFile) {
          const logoUrl = await uploadWorkspaceLogo(workspace.id);
          await sheetWorkspaceService.updateWorkspaceBasics(workspace.id, { logoUrl });
          nextWorkspace = { ...workspace, ...basicUpdates, ...routingUpdates, logoUrl };
        } else if (workspaceLogoRemoved && `${workspace?.logoUrl || ''}`.trim()) {
          await sheetWorkspaceService.updateWorkspaceBasics(workspace.id, { logoUrl: null });
          nextWorkspace = { ...workspace, ...basicUpdates, ...routingUpdates, logoUrl: '' };
        } else {
          nextWorkspace = { ...workspace, ...basicUpdates, ...routingUpdates };
        }

        await syncWorkspaceMembers(workspace.id);
        setWorkspace(nextWorkspace);
        toast.success('Workspace updated.');
      } else {
        const createdWorkspace = await sheetWorkspaceService.createWorkspace(basePayload, user?.uid);
        let logoUrl = '';

        if (workspaceLogoFile) {
          logoUrl = await uploadWorkspaceLogo(createdWorkspace.id);
          await sheetWorkspaceService.updateWorkspaceBasics(createdWorkspace.id, { logoUrl });
        }

        if (workspaceVisibility === 'restricted' && workspaceIsTemplate !== true) {
          await syncWorkspaceMembers(createdWorkspace.id);
        }

        nextWorkspace = logoUrl ? { ...createdWorkspace, ...basePayload, logoUrl } : { ...createdWorkspace, ...basePayload };

        if (assignmentSheetId) {
          await sheetService.updateSheet(assignmentSheetId, {
            workspaceId: createdWorkspace.id,
            workspaceTitle: createdWorkspace.title || ''
          });
          toast.success(`Workspace created and ${assignmentSheetTitle || 'sheet'} was added.`);
        } else {
          toast.success('Workspace created.');
        }
      }

      await savePendingAiCredentials(nextWorkspace.id);

      if (isEditing && workspaceCustomDomainEdited && cleanCustomDomain && cleanCustomDomain !== previousCustomDomain) {
        window.location.assign(getWorkspaceUrl({ ...nextWorkspace, customDomain: cleanCustomDomain }));
        return;
      }

      navigate(getWorkspacePath(cleanSlug));
    } catch (saveError) {
      toast.error(saveError?.message || `Failed to ${isEditing ? 'update' : 'create'} workspace.`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white px-6 py-10 text-center text-slate-500 shadow-sm">
        Loading workspace builder...
      </div>
    );
  }

  if (isEditing && loadError) {
    return (
      <div className="space-y-3 w-full max-w-6xl mx-auto">
        <SheetsHeader
          activeTab="workspaces"
          badge="Workspace Builder"
          backPath={backPath}
          backAriaLabel="Back"
          title="Workspace Builder"
          description="The requested workspace editor could not be opened."
        />
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-6 py-5 text-rose-700 shadow-sm">
          {loadError}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3 w-full max-w-6xl mx-auto" style={workspaceThemeStyle}>
      <SheetsHeader
        activeTab="workspaces"
        badge="Workspace Builder"
        backPath={backPath}
        backAriaLabel="Back to workspaces"
        title={pageTitle}
        description={pageDescription}
        actions={(
          <>
            <button
              type="button"
              onClick={() => navigate(backPath)}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              <ArrowLeft className="h-4 w-4" />
              {isEditing ? 'Back To Workspace' : 'Back To Workspaces'}
            </button>
            <button
              type="button"
              onClick={handleSaveWorkspace}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-xl bg-[var(--workspace-primary-color)] px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Sparkles className="h-4 w-4" />
              {saving ? (isEditing ? 'Saving...' : 'Creating...') : (isEditing ? 'Save Workspace' : 'Create Workspace')}
            </button>
          </>
        )}
      />

      <nav className="flex max-w-full gap-1 overflow-x-auto rounded-xl border border-slate-200 bg-white p-1 shadow-sm" aria-label="Workspace builder panels">
        {[
          { id: 'setup', label: 'Workspace Setup' },
          { id: 'access', label: 'Access' },
          { id: 'dashboard', label: 'Dashboard' },
          { id: 'ai', label: 'AI Skills' },
          { id: 'agents', label: 'Agents' }
        ].map((panel) => {
          const isActive = activeBuilderPanel === panel.id;
          return (
            <button
              key={panel.id}
              type="button"
              onClick={() => setActiveBuilderPanel(panel.id)}
              aria-current={isActive ? 'page' : undefined}
              className={`shrink-0 rounded-xl px-4 py-2.5 text-sm font-semibold transition ${
                isActive
                  ? 'bg-[var(--workspace-primary-color)] text-white shadow-sm'
                  : 'text-[var(--workspace-primary-color)] opacity-80 hover:bg-slate-50 hover:opacity-100'
              }`}
            >
              {panel.label}
            </button>
          );
        })}
      </nav>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.45fr)_360px]">
        <div className="space-y-6">
          {activeBuilderPanel === 'setup' && (
          <section className="rounded-[10px] border border-slate-200 bg-white p-6 shadow-md">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Identity</p>
                <h2 className="mt-2 text-2xl font-bold text-slate-900">Workspace Basics</h2>
                <p className="mt-1 text-sm text-slate-500">
                  Give the workspace its own route, description, and default visual identity.
                </p>
              </div>
              <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-slate-200 bg-slate-50">
                <img
                  src={logoPreviewSrc}
                  alt="Workspace logo preview"
                  className="h-full w-full object-contain p-2"
                />
              </div>
            </div>

            <div className="mt-5 grid gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <label className="mb-2 block text-sm font-medium text-slate-700">Workspace Name</label>
                <input
                  value={workspaceTitle}
                  onChange={(e) => setWorkspaceTitle(e.target.value)}
                  placeholder="Client Ops"
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                />
              </div>

              <div className="md:col-span-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium text-slate-700">Admin builder access</p>
                    <p className="mt-1 text-xs text-slate-500">
                      Allow platform admins to find this workspace in the admin access section and open its management flow without exposing sheet rows or rendered workspace views by default.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setAdminBuilderAccessEnabled((prev) => !prev)}
                    className={`inline-flex h-7 w-12 items-center rounded-full border transition ${
                      adminBuilderAccessEnabled
                        ? 'border-[#008080] bg-[#008080]'
                        : 'border-slate-300 bg-slate-200'
                    }`}
                    aria-pressed={adminBuilderAccessEnabled}
                  >
                    <span
                      className={`inline-block h-5 w-5 rounded-full bg-white shadow transition ${
                        adminBuilderAccessEnabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">Workspace Path</label>
                <div className="relative">
                  <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-sm text-slate-400">
                    {workspacePathPrefix}
                  </span>
                  <input
                    value={workspaceSlug}
                    onChange={(e) => {
                      setWorkspaceSlugEdited(true);
                      setWorkspaceSlug(normalizeWorkspaceSlug(e.target.value, ''));
                    }}
                    placeholder="client-ops"
                    className="w-full rounded-2xl border border-slate-200 py-3 pl-[9.5rem] pr-4 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                  />
                </div>
                <div className="mt-2 flex items-center gap-2 text-xs">
                  <Link2 className="h-3.5 w-3.5 text-slate-400" />
                  <span className={
                    workspaceSlugStatus.state === 'available'
                      ? 'text-emerald-600'
                      : workspaceSlugStatus.state === 'unavailable'
                        ? 'text-rose-600'
                        : 'text-slate-500'
                  }>
                    {workspaceSlugStatus.message}
                  </span>
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">Custom Domain</label>
                <input
                  value={workspaceCustomDomain}
                  onChange={(e) => {
                    setWorkspaceCustomDomainEdited(true);
                    setWorkspaceCustomDomain(e.target.value);
                  }}
                  placeholder="workspace.yourdomain.com"
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                />
              </div>

              <div className="md:col-span-2">
                <label className="mb-2 block text-sm font-medium text-slate-700">Description</label>
                <textarea
                  value={workspaceDescription}
                  onChange={(e) => setWorkspaceDescription(e.target.value)}
                  rows={4}
                  placeholder="Operational context, team purpose, or client summary."
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                />
              </div>

              <div className="md:col-span-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
                <p className="text-sm font-medium text-slate-700">Organisation access</p>
                <p className="mt-1 text-xs text-slate-500">Approved members may enter this workspace, but they only see sheets assigned to one of the same organisations.</p>
                {availableOrganizations.length ? (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {availableOrganizations.map((organization) => {
                      const checked = organizationIds.includes(organization.id);
                      return (
                        <label key={`workspace_org_${organization.id}`} className={`inline-flex cursor-pointer items-center gap-2 rounded-full border px-3 py-2 text-sm ${checked ? 'border-[#008080] bg-[#008080]/10 text-[#0f4f4f]' : 'border-slate-200 bg-white text-slate-700'}`}>
                          <input type="checkbox" checked={checked} onChange={() => setOrganizationIds((current) => checked ? current.filter((idValue) => idValue !== organization.id) : [...current, organization.id])} className="h-4 w-4 rounded border-slate-300 text-[#008080] focus:ring-[#008080]" />
                          <span className="font-semibold">{organization.name}</span>
                          {userProfile?.activeOrganizationId === organization.id && <span className="text-[10px] font-bold uppercase text-[#008080]">Active</span>}
                        </label>
                      );
                    })}
                  </div>
                ) : (
                  <p className="mt-3 text-sm text-slate-500">No approved organisation memberships are available.</p>
                )}
              </div>

              <div className="md:col-span-2">
                <label className="mb-2 block text-sm font-medium text-slate-700">Primary Color</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={normalizeWorkspacePrimaryColor(workspacePrimaryColor) || DEFAULT_WORKSPACE_PRIMARY_COLOR}
                    onChange={(e) => setWorkspacePrimaryColor(e.target.value.toUpperCase())}
                    className="h-12 w-14 cursor-pointer rounded-xl border border-slate-200 bg-white p-1"
                    aria-label="Choose workspace primary color"
                  />
                  <input
                    value={workspacePrimaryColor}
                    onChange={(e) => setWorkspacePrimaryColor(e.target.value)}
                    placeholder="#008080"
                    className="min-w-0 flex-1 rounded-2xl border border-slate-200 px-4 py-3 text-sm uppercase text-slate-900 outline-none transition focus:border-[#008080]"
                  />
                </div>
                <p className="mt-2 text-xs text-slate-500">
                  Enter a 3 or 6 digit hex code. This color is used for accents on the workspace page.
                </p>
              </div>
            </div>

            <div className="mt-5 flex flex-wrap gap-2">
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (!file.type.startsWith('image/')) {
                      toast.error('Choose an image file for the workspace logo.');
                      e.target.value = '';
                      return;
                    }
                    setWorkspaceLogoRemoved(false);
                    setWorkspaceLogoFile(file);
                    e.target.value = '';
                  }}
                />
                <ImagePlus className="h-4 w-4" />
                Upload Logo
              </label>
              <button
                type="button"
                onClick={() => {
                  setWorkspaceLogoFile(null);
                  setWorkspaceLogoRemoved(true);
                }}
                className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                <RotateCcw className="h-4 w-4" />
                Use Default
              </button>
            </div>
          </section>
          )}
          {activeBuilderPanel === 'access' && (
          <section className="rounded-[10px] border border-slate-200 bg-white p-6 shadow-md">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Access</p>
            <h2 className="mt-2 text-2xl font-bold text-slate-900">Visibility And Collaboration</h2>
            <p className="mt-1 text-sm text-slate-500">
              Define who can see the workspace and how sheet collaboration should behave by default.
            </p>

            <div className="mt-5 grid gap-4 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">Visibility</label>
                <select
                  value={workspaceIsTemplate ? 'template' : workspaceVisibility}
                  onChange={(e) => {
                    const nextValue = e.target.value;
                    if (nextValue === 'template') {
                      setWorkspaceIsTemplate(true);
                      setWorkspaceVisibility('private');
                      return;
                    }
                    setWorkspaceIsTemplate(false);
                    setWorkspaceVisibility(nextValue);
                  }}
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                >
                  <option value="private">Private</option>
                  <option value="restricted">Restricted</option>
                  <option value="template">Template</option>
                </select>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">Default Collaborator Access</label>
                <select
                  value={workspaceAccess}
                  onChange={(e) => setWorkspaceAccess(e.target.value)}
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                >
                  <option value="view">View</option>
                  <option value="edit_own_rows">Edit own rows</option>
                  <option value="edit">Edit</option>
                </select>
              </div>
            </div>

            <label className="mt-5 flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={allowCrossSheetReferences}
                onChange={(e) => setAllowCrossSheetReferences(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-[#008080] focus:ring-[#008080]"
              />
              <span>
                Allow cross-sheet references inside this workspace so formulas, logs, and actions can stay workspace-scoped.
              </span>
            </label>

            {workspaceVisibility === 'restricted' && workspaceIsTemplate !== true && (
              <div className="mt-5 grid gap-5 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="flex items-start gap-3">
                  <div className="rounded-xl bg-white p-2 text-[#008080]">
                    <Users className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Allowed Workspace Members</p>
                    <p className="mt-1 text-xs text-slate-500">
                      Add signed-in users by email. Checked matches will be synchronized as workspace members when you save.
                    </p>
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">Allowed Emails</label>
                  <input
                    value={allowedEmailsInput}
                    onChange={(event) => setAllowedEmailsInput(event.target.value)}
                    placeholder="name@example.com, second@example.com"
                    className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                  />
                </div>

                {resolvedSharedUsers.length > 0 && (
                  <div className="rounded-2xl border border-slate-200 bg-white p-3">
                    <p className="text-[11px] font-semibold tracking-wide text-slate-500">Matched Signed-In Users</p>
                    <div className="mt-2 space-y-2">
                      {resolvedSharedUsers.map((sharedUser) => (
                        <label
                          key={sharedUser.uid}
                          className="flex items-start gap-3 rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700"
                        >
                          <input
                            type="checkbox"
                            checked={selectedSharedUserIds.includes(sharedUser.uid)}
                            onChange={() => handleToggleSharedUser(sharedUser.uid)}
                            className="mt-1 h-4 w-4 rounded border-slate-300 text-[#008080] focus:ring-[#008080]"
                          />
                          <span>
                            <span className="block font-semibold text-slate-800">
                              {sharedUser.displayName || sharedUser.email}
                            </span>
                            <span className="block text-xs text-slate-500">
                              {sharedUser.email} | {sharedUser.uid}
                            </span>
                          </span>
                        </label>
                      ))}
                    </div>
                    <p className="mt-2 text-xs text-slate-500">
                      Unmatched emails are ignored until they map to an existing signed-in user.
                    </p>
                  </div>
                )}
              </div>
            )}
          </section>
          )}

          {activeBuilderPanel === 'dashboard' && (
          <WorkspaceDashboardBuilder
            value={dashboardConfig}
            onChange={setDashboardConfig}
            sheets={workspaceSheets}
          />
          )}

          {activeBuilderPanel === 'ai' && (
          <section className="rounded-[10px] border border-slate-200 bg-white p-6 shadow-md">
            <div className="flex items-start gap-3">
              <div className="rounded-2xl bg-[#008080]/10 p-3 text-[#008080]">
                <BrainCircuit className="h-5 w-5" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">AI Skills</p>
                <h2 className="mt-2 text-2xl font-bold text-slate-900">Workspace Skill Defaults</h2>
                <p className="mt-1 text-sm text-slate-500">
                  Store AI behavior at the workspace level so every sheet starts from the same operating context.
                </p>
              </div>
            </div>

            <label className="mt-5 inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-2 text-sm font-semibold text-slate-700">
              <input
                type="checkbox"
                checked={aiEnabled}
                onChange={(e) => setAiEnabled(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-[#008080] focus:ring-[#008080]"
              />
              Enable workspace AI skills
            </label>

            <div className="mt-5 grid gap-4">
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">Default AI Provider</label>
                <select
                  value={aiProvider}
                  onChange={(e) => setAiProvider(e.target.value === 'openai' ? 'openai' : 'gemini')}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                >
                  <option value="gemini">Google Gemini</option>
                  <option value="openai">OpenAI</option>
                </select>
                <p className="mt-2 text-xs text-slate-500">
                  Workspace AI chat uses this provider. Gemini falls back to the platform key until a workspace key is configured; OpenAI requires a workspace key.
                </p>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="flex items-start gap-3">
                  <div className="rounded-xl bg-white p-2 text-[#008080] shadow-sm">
                    <KeyRound className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Workspace API keys</p>
                    <p className="mt-1 text-xs leading-5 text-slate-500">
                      Keys are sent over the authenticated Cloud Function connection directly to Google Cloud Secret Manager. They are never saved in Firestore or returned to this page.
                    </p>
                  </div>
                </div>

                <div className="mt-4 grid gap-4">
                  {[
                    { id: 'gemini', label: 'Gemini API key', inputRef: geminiApiKeyRef, placeholder: 'Enter a new Gemini key' },
                    { id: 'openai', label: 'OpenAI API key', inputRef: openAiApiKeyRef, placeholder: 'Enter a new OpenAI key' }
                  ].map((credential) => {
                    const configured = aiCredentialStatus[credential.id] === true;
                    const isBusy = savingAiCredential === credential.id;
                    return (
                      <div key={credential.id} className="rounded-xl border border-slate-200 bg-white p-4">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <label className="text-sm font-medium text-slate-700" htmlFor={`workspace-${credential.id}-api-key`}>
                            {credential.label}
                          </label>
                          <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                            configured
                              ? 'bg-emerald-50 text-emerald-700'
                              : 'bg-slate-100 text-slate-500'
                          }`}>
                            {aiCredentialStatusLoading ? 'Checking...' : (configured ? 'Configured' : 'Not configured')}
                          </span>
                        </div>
                        <input
                          id={`workspace-${credential.id}-api-key`}
                          ref={credential.inputRef}
                          type="password"
                          autoComplete="new-password"
                          spellCheck={false}
                          placeholder={configured ? 'Enter a replacement key' : credential.placeholder}
                          className="mt-3 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                        />
                        <div className="mt-3 flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => handleSaveAiCredential(credential.id)}
                            disabled={!workspace?.id || isBusy}
                            className="rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white transition hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {isBusy ? 'Saving...' : (configured ? 'Rotate key' : 'Store key')}
                          </button>
                          {configured && (
                            <button
                              type="button"
                              onClick={() => handleRemoveAiCredential(credential.id)}
                              disabled={isBusy}
                              className="inline-flex items-center gap-1.5 rounded-lg border border-rose-200 px-3 py-2 text-xs font-semibold text-rose-700 transition hover:bg-rose-50 disabled:opacity-50"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                              Remove
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {!workspace?.id && (
                  <p className="mt-3 text-xs text-amber-700">
                    Enter keys now and use Create Workspace; they will be stored after the workspace is created.
                  </p>
                )}
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">Skill Presets</label>
                <input
                  value={aiSkills}
                  onChange={(e) => setAiSkills(e.target.value)}
                  placeholder="reporting, client-summary, intake-triage"
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                />
                <p className="mt-2 text-xs text-slate-500">
                  Comma-separated labels for the reusable AI skills this workspace should expose first.
                </p>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">Workspace AI Instructions</label>
                <textarea
                  value={aiInstructions}
                  onChange={(e) => setAiInstructions(e.target.value)}
                  rows={4}
                  placeholder="Describe tone, business context, formatting rules, and what the AI should prioritize across this workspace."
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                />
              </div>
            </div>
          </section>
          )}

          {activeBuilderPanel === 'agents' && (
          <section className="rounded-[10px] border border-slate-200 bg-white p-6 shadow-md">
            <div className="flex items-start gap-3">
              <div className="rounded-2xl bg-slate-100 p-3 text-slate-700">
                <Bot className="h-5 w-5" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Agents</p>
                <h2 className="mt-2 text-2xl font-bold text-slate-900">Workspace Agent Defaults</h2>
                <p className="mt-1 text-sm text-slate-500">
                  Reserve a place for workspace-level agents instead of forcing those settings into each sheet later.
                </p>
              </div>
            </div>

            <label className="mt-5 inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-2 text-sm font-semibold text-slate-700">
              <input
                type="checkbox"
                checked={agentsEnabled}
                onChange={(e) => setAgentsEnabled(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-[#008080] focus:ring-[#008080]"
              />
              Enable workspace agents
            </label>

            <div className="mt-5 grid gap-4 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">Default Agent Name</label>
                <input
                  value={agentName}
                  onChange={(e) => setAgentName(e.target.value)}
                  placeholder="Ops Coordinator"
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">Automation Level</label>
                <select
                  value={agentAutomationLevel}
                  onChange={(e) => setAgentAutomationLevel(e.target.value)}
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                >
                  <option value="manual">Manual</option>
                  <option value="suggest">Suggest actions</option>
                  <option value="auto">Automatic</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="mb-2 block text-sm font-medium text-slate-700">Attached Skills</label>
                <input
                  value={agentSkills}
                  onChange={(e) => setAgentSkills(e.target.value)}
                  placeholder="reporting, client-summary"
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                />
                <p className="mt-2 text-xs text-slate-500">
                  Comma-separated workspace skills this agent can use. Available presets: {parseSkills(aiSkills).join(', ') || 'add presets in AI Skills first'}.
                </p>
              </div>

              <div className="md:col-span-2">
                <label className="mb-2 block text-sm font-medium text-slate-700">Agent Instructions</label>
                <textarea
                  value={agentInstructions}
                  onChange={(e) => setAgentInstructions(e.target.value)}
                  rows={4}
                  placeholder="Describe the agent role, guardrails, escalation behavior, and what this workspace should automate."
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                />
              </div>
            </div>
          </section>
          )}
        </div>

        <aside className="space-y-6">
          <section className="rounded-[10px] border border-slate-200 bg-white p-6 shadow-md">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Summary</p>
            <h2 className="mt-2 text-xl font-bold text-slate-900">{workspaceTitle.trim() || 'New workspace'}</h2>
            <p className="mt-2 text-sm text-slate-500">{setupSummary}</p>

            <div className="mt-5 space-y-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
              <div className="flex items-center justify-between gap-3">
                <span>Path</span>
                <span className="font-semibold text-slate-900">/{workspaceSlug || 'workspace-path'}</span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span>Visibility</span>
                <span className="font-semibold capitalize text-slate-900">{workspaceIsTemplate ? 'template' : workspaceVisibility}</span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span>Default access</span>
                <span className="font-semibold text-slate-900">{workspaceAccess}</span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span>AI skills</span>
                <span className="font-semibold text-slate-900">{aiEnabled ? 'Enabled' : 'Off'}</span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span>Agents</span>
                <span className="font-semibold text-slate-900">{agentsEnabled ? 'Enabled' : 'Off'}</span>
              </div>
            </div>

            {assignmentSheetId && (
              <div className="mt-5 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                When this workspace is created, <span className="font-semibold">{assignmentSheetTitle || 'the current sheet'}</span> will be added to it automatically.
              </div>
            )}
          </section>

          <section className="rounded-[10px] border border-slate-200 bg-white p-6 shadow-md">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Why This Page</p>
            <ul className="mt-3 space-y-3 text-sm text-slate-600">
              <li>Workspace creation and editing live in the same dedicated builder.</li>
              <li>Routing, branding, AI skills, agent defaults, and access controls stay out of a cramped modal.</li>
              <li>This gives workspace-level configuration room to grow without bloating the workspace page.</li>
            </ul>
          </section>
        </aside>
      </div>
    </div>
  );
};

export default WorkspaceBuilder;
